import React, { useState } from "react";
import * as Icons from "lucide-react";
import { CompassLogo } from "@/components/CompassLogo";
import { useLang } from "@/i18n/LanguageContext";
import { UI, t } from "@/i18n/translations";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Onboarding({ onDone }) {
  const { lang } = useLang();
  const { setUser } = useAuth();
  const O = UI.onboarding;
  const slides = O.slides;
  const [i, setI] = useState(0);
  const last = i === slides.length - 1;

  const finish = async () => {
    try {
      const { data } = await api.post("/user/state", { onboarding_completed: true });
      setUser(data);
    } catch (err) {
      console.error("Failed to persist onboarding completion:", err);
    }
    onDone?.();
  };

  const slide = slides[i];
  const Icon = Icons[slide.icon] || Icons.Compass;

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col" data-testid="onboarding">
      <div className="flex items-center justify-between px-5 h-16">
        <div className="flex items-center gap-2"><CompassLogo size={28} /><span className="font-display font-extrabold">Boussole</span></div>
        {!last && (
          <button data-testid="onboarding-skip" onClick={finish} className="text-sm font-semibold text-ink/50 hover:text-ink">{t(O.skip, lang)}</button>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center max-w-md mx-auto w-full">
        <div key={i} className="animate-fade-up flex flex-col items-center">
          <div className="grid place-items-center w-24 h-24 rounded-[28px] bg-ink mb-8">
            <Icon className="w-11 h-11 text-amber" strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(slide.title, lang)}</h1>
          <p className="text-base text-ink/65 mt-4 leading-relaxed">{t(slide.body, lang)}</p>
        </div>
      </div>

      <div className="px-8 pb-10 max-w-md mx-auto w-full">
        <div className="flex justify-center gap-2 mb-6">
          {slides.map((_, idx) => (
            <span key={idx} className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-amber" : "w-2 bg-line"}`} />
          ))}
        </div>
        <button
          data-testid="onboarding-next"
          onClick={() => (last ? finish() : setI(i + 1))}
          className="w-full rounded-2xl bg-amber text-ink font-bold py-4 hover:brightness-105 active:scale-[0.99] transition"
        >
          {last ? t(O.start, lang) : t(O.next, lang)}
        </button>
      </div>
    </div>
  );
}
